import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

const emptyAssignment = {
  id: null,
  employeeId: "",
  positionId: "",
  locationId: "",
  startsAt: "",
  endsAt: "",
  hourlyRate: "",
  netHourlyRate: "",
  status: "PLANNED",
  source: "MANUAL",
  notes: "",
  overrideRuleViolations: false,
  overrideReason: "",
  version: null
};

const toInputDateTime = (value) => value ? value.slice(0, 16) : "";

const addDays = (dateString, days) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const policyLabels = {
  EMPLOYMENT: "umowa o pracę",
  CIVIL: "umowa cywilnoprawna",
  OTHER: "inne / nieokreślone"
};

const costRateSourceLabels = {
  MANUAL: "ręczne nadpisanie",
  EMPLOYEE_POSITION: "stawka pracownika na stanowisku",
  CONTRACT: "aktywna umowa",
  POSITION: "stawka bazowa stanowiska"
};

const insuranceProfileLabels = {
  STANDARD_ZUS: "ZUS",
  NO_ZUS: "bez składek pracodawcy",
  STUDENT_PUPIL: "uczeń / student",
  KRUS: "KRUS",
  UNKNOWN: "nieokreślony"
};

const FieldLabelWithTooltip = ({ label, children }) => (
  <span className="schedule-column-header">
    <span>{label}</span>
    <OverlayTrigger
      placement="top"
      container={typeof document !== "undefined" ? document.body : undefined}
      popperConfig={{ strategy: "fixed" }}
      overlay={(
        <Tooltip className="schedule-column-tooltip">
          <div className="schedule-tooltip-content">{children}</div>
        </Tooltip>
      )}
    >
      <button type="button" className="schedule-help-trigger" aria-label={`Pomoc: ${label}`}>?</button>
    </OverlayTrigger>
  </span>
);

const ShiftAssignmentForm = ({ schedule, referenceData, assignment, initialEmployeeId, initialDate, onSave, onCancel, saving }) => {
  const axiosPrivate = useAxiosPrivate();
  const [form, setForm] = useState(emptyAssignment);
  const [eligibility, setEligibility] = useState({});
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [eligibilityError, setEligibilityError] = useState("");
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (assignment) {
      setForm({
        id: assignment.id,
        employeeId: String(assignment.employeeId ?? ""),
        positionId: String(assignment.positionId ?? ""),
        locationId: String(assignment.locationId ?? ""),
        startsAt: toInputDateTime(assignment.startsAt),
        endsAt: toInputDateTime(assignment.endsAt),
        hourlyRate: assignment.hourlyRate ?? "",
        netHourlyRate: assignment.netHourlyRate ?? "",
        status: assignment.status ?? "PLANNED",
        source: assignment.source ?? "MANUAL",
        notes: assignment.notes ?? "",
        overrideRuleViolations: assignment.ruleOverride === true,
        overrideReason: assignment.ruleOverrideReason ?? "",
        version: assignment.version ?? null
      });
    } else {
      setForm({
        ...emptyAssignment,
        employeeId: initialEmployeeId == null ? "" : String(initialEmployeeId)
      });
    }
    setEligibility({});
    setEligibilityError("");
  }, [assignment, initialEmployeeId, initialDate]);

  useEffect(() => {
    let active = true;
    axiosPrivate.get("/locations")
      .then(response => { if (active) setLocations(response.data || []); })
      .catch(() => { if (active) setLocations([]); });
    return () => { active = false; };
  }, [axiosPrivate]);

  const employees = referenceData?.employees ?? [];
  const positions = useMemo(
    () => (referenceData?.positions ?? []).filter(position => position.active !== false),
    [referenceData]
  );

  useEffect(() => {
    setEligibility({});
    setEligibilityError("");

    if (form.status === "CANCELLED" || !form.positionId || !form.startsAt || !form.endsAt) return;
    if (new Date(form.endsAt).getTime() <= new Date(form.startsAt).getTime()) return;

    let active = true;
    const timer = setTimeout(async () => {
      setEligibilityLoading(true);
      try {
        const params = {
          positionId: Number(form.positionId),
          startsAt: form.startsAt,
          endsAt: form.endsAt
        };
        if (assignment?.id) params.excludeAssignmentId = assignment.id;

        const response = await axiosPrivate.get(`/work-schedules/${schedule.id}/eligibility`, { params });
        if (!active) return;

        const eligibilityMap = {};
        response.data.forEach(item => {
          eligibilityMap[item.employeeId] = item;
        });
        setEligibility(eligibilityMap);
      } catch (error) {
        if (!active) return;
        const data = error?.response?.data;
        setEligibilityError(data?.message || (typeof data === "string" ? data : "Nie udało się sprawdzić dostępności pracowników."));
      } finally {
        if (active) setEligibilityLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [axiosPrivate, schedule.id, assignment?.id, form.positionId, form.startsAt, form.endsAt, form.status]);

  const selectedEligibility = form.employeeId ? eligibility[Number(form.employeeId)] : null;
  const selectedHardBlocked = (selectedEligibility?.hardReasons?.length ?? 0) > 0;
  const selectedRuleViolations = selectedEligibility?.ruleViolations ?? [];
  const selectedNeedsOverride = selectedRuleViolations.length > 0 && !selectedHardBlocked;
  const selectedWarnings = selectedEligibility?.warnings ?? [];
  const selectedLaborCost = selectedEligibility?.laborCost ?? null;
  const hasManualRateOverride = form.hourlyRate !== "" && (
    !assignment
    || assignment.costRateSource === "MANUAL"
    || Number(form.hourlyRate) !== Number(assignment.hourlyRate)
  );
  const displayedLaborCost = useMemo(() => {
    if (!selectedLaborCost) return null;
    if (!hasManualRateOverride || !selectedLaborCost.complete) return selectedLaborCost;
    const wageRate = Number(form.hourlyRate);
    if (!Number.isFinite(wageRate) || wageRate < 0) return selectedLaborCost;
    const contributionPercent = Number(selectedLaborCost.employerContributionPercent ?? 0);
    const commissionPercent = Number(selectedLaborCost.agencyCommissionPercent ?? 0);
    const contributions = Number((wageRate * contributionPercent / 100).toFixed(2));
    const commission = Number((wageRate * commissionPercent / 100).toFixed(2));
    return {
      ...selectedLaborCost,
      wageRate,
      netWageRate: form.netHourlyRate === "" ? null : Number(form.netHourlyRate),
      wageRateSource: "MANUAL",
      employerContributionsHourly: contributions,
      agencyCommissionHourly: commission,
      totalHourlyCost: Number((wageRate + contributions + commission).toFixed(2))
    };
  }, [selectedLaborCost, form.hourlyRate, form.netHourlyRate, hasManualRateOverride]);
  const overrideReasonRequired = referenceData?.ruleOverrideRequireReason !== false;
  const eligibilityResults = Object.values(eligibility);
  const availableCount = eligibilityResults.filter(item => item.eligible).length;
  const overrideableCount = eligibilityResults.filter(item => item.overrideAllowed).length;
  const hardBlockedCount = eligibilityResults.filter(item => (item.hardReasons?.length ?? 0) > 0).length;

  const hardReasonSummary = useMemo(() => {
    const counts = new Map();
    Object.values(eligibility).forEach(result => {
      (result.hardReasons ?? []).forEach(reason => {
        counts.set(reason, (counts.get(reason) ?? 0) + 1);
      });
    });
    return [...counts.entries()]
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason, "pl"));
  }, [eligibility]);

  const ruleReasonSummary = useMemo(() => {
    const counts = new Map();
    Object.values(eligibility).forEach(result => {
      if ((result.hardReasons?.length ?? 0) > 0) return;
      (result.ruleViolations ?? []).forEach(reason => {
        counts.set(reason, (counts.get(reason) ?? 0) + 1);
      });
    });
    return [...counts.entries()]
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason, "pl"));
  }, [eligibility]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm(current => {
      if (name === "status" && value === "OPEN") {
        return { ...current, status: value, employeeId: "", overrideRuleViolations: false, overrideReason: "" };
      }
      if (name === "positionId") {
        const selectedPosition = positions.find(position => Number(position.id) === Number(value));
        return {
          ...current,
          positionId: value,
          locationId: selectedPosition?.defaultLocationId ? String(selectedPosition.defaultLocationId) : "",
          employeeId: !assignment && initialEmployeeId != null ? String(initialEmployeeId) : "",
          overrideRuleViolations: false,
          overrideReason: ""
        };
      }
      if (name === "employeeId") {
        return { ...current, employeeId: value, overrideRuleViolations: false, overrideReason: "" };
      }
      return { ...current, [name]: type === "checkbox" ? checked : value };
    });
  };

  const submit = (event) => {
    event.preventDefault();
    if (selectedHardBlocked) return;
    if (selectedNeedsOverride && !form.overrideRuleViolations) return;
    if (hasManualRateOverride && (!Number.isFinite(Number(form.hourlyRate)) || Number(form.hourlyRate) < 0)) return;
    if (hasManualRateOverride && form.netHourlyRate !== "" && (!Number.isFinite(Number(form.netHourlyRate)) || Number(form.netHourlyRate) < 0)) return;

    onSave({
      employeeId: form.employeeId === "" ? null : Number(form.employeeId),
      positionId: Number(form.positionId),
      locationId: form.locationId === "" ? null : Number(form.locationId),
      startsAt: form.startsAt,
      endsAt: form.endsAt,
      hourlyRate: hasManualRateOverride ? Number(form.hourlyRate) : null,
      netHourlyRate: hasManualRateOverride && form.netHourlyRate !== "" ? Number(form.netHourlyRate) : null,
      status: form.status,
      source: form.status === "OPEN" ? "OPEN_SHIFT" : "MANUAL",
      notes: form.notes,
      overrideRuleViolations: selectedNeedsOverride && form.overrideRuleViolations,
      overrideReason: selectedNeedsOverride && form.overrideRuleViolations ? form.overrideReason : null,
      version: form.version
    });
  };

  const employeeLabel = (employee) => {
    const result = eligibility[employee.id];
    if (!result) return employee.fullName;
    if ((result.hardReasons?.length ?? 0) > 0) return `${employee.fullName} — ${result.hardReasons.join("; ")}`;
    if ((result.ruleViolations?.length ?? 0) > 0) return `${employee.fullName} — ⚠ wymaga odstępstwa: ${result.ruleViolations.join("; ")}`;
    if (result.preferredAvailability) return `${employee.fullName} — ★ preferowana dostępność`;
    return employee.fullName;
  };

  const metrics = selectedEligibility && form.status === "PLANNED" ? (
    <div className="schedule-eligibility-metrics mt-2">
      <span>Profil umowy: <strong>{policyLabels[selectedEligibility.contractPolicy] ?? selectedEligibility.contractPolicy}</strong></span>
      {displayedLaborCost && (
        <>
          <span>Stawka brutto: <strong>{displayedLaborCost.wageRate == null ? "brak" : `${Number(displayedLaborCost.wageRate).toFixed(2)} zł/h`}</strong>
            {displayedLaborCost.wageRateSource ? ` · ${costRateSourceLabels[displayedLaborCost.wageRateSource] ?? displayedLaborCost.wageRateSource}` : ""}
          </span>
          <span>Stawka netto: <strong>{displayedLaborCost.netWageRate == null ? "brak" : `${Number(displayedLaborCost.netWageRate).toFixed(2)} zł/h`}</strong></span>
          <span>Koszt pracodawcy: <strong>{displayedLaborCost.totalHourlyCost == null ? "niepełny" : `${Number(displayedLaborCost.totalHourlyCost).toFixed(2)} zł/h`}</strong>
            {displayedLaborCost.insuranceProfile ? ` · ${insuranceProfileLabels[displayedLaborCost.insuranceProfile] ?? displayedLaborCost.insuranceProfile}` : ""}
          </span>
          <span>Składki pracodawcy: <strong>{displayedLaborCost.employerContributionsHourly == null ? "—" : `${Number(displayedLaborCost.employerContributionsHourly).toFixed(2)} zł/h`}</strong>
            {displayedLaborCost.agencyCommissionHourly != null && Number(displayedLaborCost.agencyCommissionHourly) > 0 ? ` · prowizja agencji ${Number(displayedLaborCost.agencyCommissionHourly).toFixed(2)} zł/h` : ""}
          </span>
        </>
      )}
      {selectedEligibility.preferredAvailability && <span><strong>★ Preferowana dostępność dla tej zmiany</strong></span>}
      <span>Tydzień po przypisaniu: <strong>{selectedEligibility.weeklyHoursAfter ?? 0} h</strong>
        {selectedEligibility.maximumWeeklyHours != null ? ` / max ${selectedEligibility.maximumWeeklyHours} h` : ""}
        {selectedEligibility.minimumWeeklyHours != null ? ` / min ${selectedEligibility.minimumWeeklyHours} h` : ""}
      </span>
      <span>Dni pracy w tygodniu: <strong>{selectedEligibility.workingDaysInWeekAfter ?? 0}</strong>
        {selectedEligibility.requiredDaysOffPerWeek != null ? `, wolne min. ${selectedEligibility.requiredDaysOffPerWeek}` : ""}
      </span>
      <span>Kolejne dni pracy: <strong>{selectedEligibility.consecutiveWorkDaysAfter ?? 0}</strong>
        {selectedEligibility.maximumConsecutiveWorkDays != null ? ` / max ${selectedEligibility.maximumConsecutiveWorkDays}` : ""}
      </span>
    </div>
  ) : null;

  const diagnostics = !eligibilityLoading && Object.keys(eligibility).length > 0 ? (
    <div className="alert alert-light border mb-3 py-2 px-3">
      <strong>Diagnostyka obsady</strong>
      {availableCount === 0 && overrideableCount === 0 && (
        <p className="mb-2 mt-1">
          Dla tego stanowiska i tych godzin nie ma obecnie żadnego pracownika, którego można przypisać.
          Twardych blokad nie da się obejść ręcznym zatwierdzeniem — trzeba poprawić dane pracownika, termin lub godziny albo wymagane stanowisko.
        </p>
      )}
      {availableCount === 0 && overrideableCount > 0 && (
        <p className="mb-2 mt-1">
          Nie ma pracowników dostępnych bez odstępstw, ale {overrideableCount} {overrideableCount === 1 ? "osoba może" : "osoby mogą"} zostać przypisana ręcznie po świadomym zaakceptowaniu naruszenia reguły czasu pracy i podaniu uzasadnienia.
        </p>
      )}
      {availableCount > 0 && (
        <p className="mb-2 mt-1">
          Obecnie istnieją kandydaci możliwi do przypisania. Zmień status na „Zaplanowana” i wybierz pracownika.
        </p>
      )}

      {(hardReasonSummary.length > 0 || ruleReasonSummary.length > 0) && (
        <details>
          <summary>Dlaczego kandydaci odpadają?</summary>
          {hardReasonSummary.length > 0 && (
            <>
              <div className="fw-semibold mt-2">Twarde blokady — wymagają poprawienia danych lub terminu:</div>
              <ul className="mb-2">
                {hardReasonSummary.map(item => (
                  <li key={`hard-${item.reason}`}>{item.reason} — {item.count} os.</li>
                ))}
              </ul>
            </>
          )}
          {ruleReasonSummary.length > 0 && (
            <>
              <div className="fw-semibold mt-2">Reguły możliwe do ręcznego odstępstwa:</div>
              <ul className="mb-0">
                {ruleReasonSummary.map(item => (
                  <li key={`rule-${item.reason}`}>{item.reason} — {item.count} os.</li>
                ))}
              </ul>
            </>
          )}
        </details>
      )}
    </div>
  ) : null;

  return (
    <form className="schedule-assignment-form" onSubmit={submit}>
      {diagnostics}
      <div className="schedule-assignment-form-grid">
        <Form.Group>
          <Form.Label>
            <FieldLabelWithTooltip label="Stanowisko">
              <span>Stanowisko określa rodzaj wykonywanej pracy, a nie miejsce jej wykonywania.</span>
              <span>To samo stanowisko może występować w różnych lokalizacjach w kolejnych odcinkach dnia.</span>
            </FieldLabelWithTooltip>
          </Form.Label>
          <Form.Select name="positionId" value={form.positionId} onChange={handleChange} required>
            <option value="">Wybierz stanowisko</option>
            {positions.map(position => (
              <option key={position.id} value={position.id}>{position.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <Form.Label>
            <FieldLabelWithTooltip label="Lokalizacja">
              <span>To miejsce dotyczy tylko tego odcinka pracy.</span>
              <span>Jedno stanowisko może występować w wielu lokalizacjach; kolejną lokalizację zaplanuj jako następną zmianę bez nakładania godzin.</span>
            </FieldLabelWithTooltip>
          </Form.Label>
          <Form.Select name="locationId" value={form.locationId} onChange={handleChange}>
            <option value="">Bez przypisanej lokalizacji</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <Form.Label>Początek</Form.Label>
          <Form.Control
            type="datetime-local"
            name="startsAt"
            min={`${!assignment && initialDate ? initialDate : schedule.dateFrom}T00:00`}
            max={`${!assignment && initialDate ? initialDate : schedule.dateTo}T23:59`}
            value={form.startsAt}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Koniec</Form.Label>
          <Form.Control
            type="datetime-local"
            name="endsAt"
            min={`${!assignment && initialDate ? initialDate : schedule.dateFrom}T00:00`}
            max={`${addDays(!assignment && initialDate ? initialDate : schedule.dateTo, 1)}T23:59`}
            value={form.endsAt}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>
            <FieldLabelWithTooltip label="Pracownik">
              {eligibilityLoading ? (
                <span>Sprawdzanie dostępności i norm czasu pracy...</span>
              ) : Object.keys(eligibility).length > 0 ? (
                <>
                  <span>Dostępni bez odstępstw: <b>{availableCount} z {employees.length}</b>.</span>
                  {overrideableCount > 0 && <span>Z ręcznym odstępstwem: <b>{overrideableCount}</b>.</span>}
                  {hardBlockedCount > 0 && <span>Twardo zablokowani: <b>{hardBlockedCount}</b>.</span>}
                </>
              ) : (
                <span>Po wybraniu stanowiska i godzin system sprawdza dostępność, kolizje oraz reguły czasu pracy kandydatów.</span>
              )}
            </FieldLabelWithTooltip>
          </Form.Label>
          <Form.Select
            name="employeeId"
            value={form.employeeId}
            onChange={handleChange}
            required={form.status !== "OPEN"}
            disabled={form.status === "OPEN" || (!assignment && initialEmployeeId != null)}
          >
            <option value="">{form.status === "OPEN" ? "Nieprzypisana" : "Wybierz pracownika"}</option>
            {employees.map(employee => {
              const result = eligibility[employee.id];
              const hardBlocked = (result?.hardReasons?.length ?? 0) > 0;
              return (
                <option key={employee.id} value={employee.id} disabled={hardBlocked}>
                  {employeeLabel(employee)}
                </option>
              );
            })}
          </Form.Select>
          {eligibilityError && <div className="text-danger small mt-1">{eligibilityError}</div>}

        </Form.Group>

        <Form.Group>
          <Form.Label>
            <FieldLabelWithTooltip label="Stawka brutto/h — opcjonalne nadpisanie">
              <span>Pozostaw puste, aby użyć stawki wyliczonej automatycznie przez centralny kalkulator kosztu.</span>
              {selectedLaborCost?.wageRate != null && (
                <span>Aktualna stawka domyślna: <b>{Number(selectedLaborCost.wageRate).toFixed(2)} zł/h</b>.</span>
              )}
            </FieldLabelWithTooltip>
          </Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            name="hourlyRate"
            value={form.hourlyRate}
            onChange={handleChange}
            placeholder={selectedLaborCost?.wageRate == null ? "" : Number(selectedLaborCost.wageRate).toFixed(2)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>
            <FieldLabelWithTooltip label="Stawka netto/h — przy ręcznym nadpisaniu">
              <span>Netto jest wartością ewidencyjną i nie jest wyliczane automatycznie z brutto.</span>
              <span>Pole jest dostępne tylko wtedy, gdy ręcznie nadpisujesz stawkę brutto tej zmiany.</span>
              {selectedLaborCost?.netWageRate != null && (
                <span>Aktualna stawka netto: <b>{Number(selectedLaborCost.netWageRate).toFixed(2)} zł/h</b>.</span>
              )}
            </FieldLabelWithTooltip>
          </Form.Label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            name="netHourlyRate"
            value={form.netHourlyRate}
            onChange={handleChange}
            disabled={!hasManualRateOverride}
            placeholder={selectedLaborCost?.netWageRate == null ? "" : Number(selectedLaborCost.netWageRate).toFixed(2)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Status</Form.Label>
          <Form.Select name="status" value={form.status} onChange={handleChange}>
            <option value="PLANNED">Zaplanowana</option>
            <option value="OPEN">Otwarta</option>
            <option value="CANCELLED">Anulowana</option>
          </Form.Select>
        </Form.Group>
      </div>

      {!assignment && initialDate && (
        <div className="schedule-calendar-prefill-info">
          Dzień wybrany w kalendarzu: <strong>{initialDate}</strong>. Data zmiany jest ograniczona do tego dnia; zmiana może zakończyć się następnego dnia.
        </div>
      )}

      {metrics}

      {selectedHardBlocked && (
        <div className="schedule-rule-block mt-3">
          <strong>Nie można przypisać pracownika:</strong>
          <ul>{selectedEligibility.hardReasons.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
        </div>
      )}

      {selectedNeedsOverride && (
        <div className="schedule-rule-override mt-3">
          <strong>Naruszenie reguł czasu pracy:</strong>
          <ul>{selectedRuleViolations.map((reason, index) => <li key={index}>{reason}</li>)}</ul>
          <Form.Check
            type="checkbox"
            name="overrideRuleViolations"
            checked={form.overrideRuleViolations}
            onChange={handleChange}
            label="Wprowadź ręczne odstępstwo od reguł czasu pracy"
          />
          {form.overrideRuleViolations && (
            <Form.Group className="mt-2">
              <Form.Label>Uzasadnienie odstępstwa</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="overrideReason"
                value={form.overrideReason}
                onChange={handleChange}
                placeholder="Powód ręcznej zmiany"
                required={overrideReasonRequired}
              />
            </Form.Group>
          )}
        </div>
      )}

      {selectedWarnings.length > 0 && (
        <div className="schedule-rule-warning mt-3">
          <strong>Ostrzeżenia:</strong>
          <ul>{selectedWarnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
        </div>
      )}

      <Form.Group className="mt-3">
        <Form.Label>Uwagi</Form.Label>
        <Form.Control as="textarea" rows={2} name="notes" value={form.notes} onChange={handleChange} />
      </Form.Group>

      <div className="schedule-form-actions mt-3">
        <Button
          type="submit"
          variant="primary"
          disabled={saving || selectedHardBlocked || (selectedNeedsOverride && (!form.overrideRuleViolations || (overrideReasonRequired && !form.overrideReason.trim())))}
        >
          {assignment ? "Zapisz pozycję grafiku" : "Dodaj pozycję grafiku"}
        </Button>
        {assignment && <Button type="button" variant="secondary" onClick={onCancel}>Anuluj edycję</Button>}
      </div>
    </form>
  );
};

export default ShiftAssignmentForm;
